import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Check, LoaderCircle, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import Footer from '@/components/Footer';
import { useCart } from '@/context/CartContext';
import { formatPrice } from '@/data/products';
import { useProducts } from '@/lib/shopify';
import { VariantPicker } from '@/components/product/VariantPicker';
import { useProductVariant } from '@/hooks/useProductVariant';
import { WishlistButton } from '@/components/WishlistButton';

function MobileGallery({ images, name }) {
  const rail = useRef(null);
  const [active, setActive] = useState(0);
  const jump = (index) => rail.current?.children[index]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  return (
    <div className="min-w-0 max-w-full overflow-hidden lg:hidden" data-testid="product-mobile-gallery">
      <div
        ref={rail}
        onScroll={(event) => setActive(Math.round(event.currentTarget.scrollLeft / event.currentTarget.clientWidth))}
        className="flex w-full max-w-full snap-x snap-mandatory overflow-x-auto"
      >
        {images.map((image, index) => (
          <div key={image.id || image.url} className="aspect-[4/5] w-full shrink-0 snap-start bg-[#F0F1EE]">
            <img src={image.url} alt={image.altText || `${name} view ${index + 1}`} className="h-full w-full object-contain" data-testid={`product-mobile-gallery-image-${index}`} />
          </div>
        ))}
      </div>
      <div className="flex snap-x snap-mandatory justify-start gap-1 px-4 py-3 overflow-x-auto" data-testid="product-gallery-pagination">
        {images.map((image, index) => (
          <button
            key={image.id || image.url}
            onClick={() => jump(index)}
            data-testid={`product-gallery-dot-${index}`}
            aria-label={`View image ${index + 1}`}
            aria-current={active === index}
            className="grid h-11 w-11 shrink-0 snap-start place-items-center transition-transform duration-200 active:scale-90"
          ><span className={`h-1.5 transition-[width,background-color] duration-300 ${active === index ? 'w-8 bg-[#1E1E1E]' : 'w-3 bg-[#C6C8C4]'}`} /></button>
        ))}
      </div>
    </div>
  );
}

function DesktopGallery({ images, name }) {
  return (
    <div className="hidden grid-cols-2 gap-3 lg:grid" data-testid="product-desktop-gallery">
      {images.map((image, index) => (
        <div key={image.id || image.url} className={`bg-[#F0F1EE] ${index === 0 && images.length % 2 ? 'col-span-2' : ''}`}>
          <img src={image.url} alt={image.altText || `${name} view ${index + 1}`} className="aspect-[4/5] h-full max-h-[86vh] w-full object-contain" data-testid={`product-desktop-gallery-image-${index}`} />
        </div>
      ))}
    </div>
  );
}

function ProductDetails({ product, variantState, onAdd }) {
  const { variants, colorOption, sizeOption, activeColor, activeSize, selected, selectColor, selectSize, selectVariant } = variantState;
  const lowStock = Number.isInteger(selected?.inventoryQuantity) && selected.inventoryQuantity > 0 && selected.inventoryQuantity <= 5;
  return <aside className="px-5 pb-24 pt-7 sm:px-8 lg:col-span-5 lg:sticky lg:top-28 lg:h-fit lg:px-4 lg:pb-0 lg:pt-0" data-testid="product-details-panel">
    <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#657260]" data-testid="product-category">{(product.collections?.map((item) => item.title).join(' / ') || product.category)} / {product.tag}</p>
    <div className="mt-4 flex items-start justify-between gap-4"><h1 className="font-syne text-4xl font-extrabold uppercase leading-[0.95] sm:text-5xl" data-testid="product-title">{product.name}</h1><WishlistButton productId={product.id} /></div>
    <div className="mt-6 flex flex-wrap items-center gap-4"><p className="font-mono2 text-lg" data-testid="product-price">{formatPrice(selected?.price ?? product.price, product.currency)}</p>{lowStock && <p className="border border-[#8A9A86] bg-[#E6EAE4] px-2 py-1 font-mono2 text-[9px] font-bold uppercase tracking-[0.16em] text-[#43533F]" data-testid="low-stock-notice">Only {selected.inventoryQuantity} left</p>}</div>
    <p className="mt-8 max-w-lg text-sm leading-7 text-[#5C5C5C]" data-testid="product-description">{product.description || 'Designed for repeat wear with a considered silhouette and an easy, expressive fit.'}</p>
    <VariantPicker variants={variants} colorOption={colorOption} sizeOption={sizeOption} activeColor={activeColor} activeSize={activeSize} selected={selected} onColorChange={selectColor} onSizeChange={selectSize} onVariantChange={selectVariant} />
    <button onClick={onAdd} disabled={!selected?.available} data-testid="product-add-to-cart-button" className="fixed inset-x-3 bottom-3 z-40 flex min-h-14 items-center justify-center gap-3 bg-[#1E1E1E] px-6 py-4 font-syne text-xs font-bold uppercase text-white shadow-[0_10px_28px_rgba(30,30,30,0.2)] transition-[background-color,transform] duration-300 active:scale-[0.98] active:bg-[#657260] disabled:opacity-40 lg:static lg:mt-8 lg:shadow-none lg:hover:-translate-y-1 lg:hover:bg-[#657260]"><ShoppingBag size={16} /> {selected?.available ? 'Add to cart' : 'Unavailable'}</button>
    <div className="mt-8 grid gap-3 border-y border-[#D1D1D6] py-6 font-mono2 text-[9px] uppercase tracking-[0.18em] text-[#5C5C5C] sm:grid-cols-2">
      <p className="flex items-center gap-2" data-testid="product-shipping-note"><Check size={13} className="text-[#657260]" /> Tracked Canada &amp; US delivery</p>
      <p className="flex items-center gap-2" data-testid="product-return-note"><Check size={13} className="text-[#657260]" /> 14-day returns</p>
    </div>
  </aside>;
}

export default function Product() {
  const { handle } = useParams();
  const { products, loading } = useProducts();
  const { add, setOpen } = useCart();
  const product = products.find((item) => item.id === handle);
  const variantState = useProductVariant(product);
  const { selected, images } = variantState;

  if (loading && !product) {
    return <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA]" data-testid="product-loading"><LoaderCircle className="animate-spin text-[#657260]" /></div>;
  }
  if (!product) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#FAFAFA] px-6 text-center" data-testid="product-not-found">
        <h1 className="font-syne text-4xl font-bold uppercase">This piece moved on.</h1>
        <Link to="/shop" data-testid="product-not-found-shop-link" className="mt-8 border-b border-[#1E1E1E] pb-1 font-mono2 text-xs uppercase">Return to shop</Link>
      </div>
    );
  }

  const addToCart = () => {
    add(product, selected);
    toast.success(`${product.name} — ${selected.title} added to cart`);
    setOpen(true);
  };

  return (
    <main className="aura-page-surface min-h-screen overflow-x-hidden text-[#1E1E1E]" data-testid="product-page">
      <div className="mx-auto max-w-[1600px] px-0 pb-32 pt-28 lg:px-8 lg:pb-28 lg:pt-36">
        <Link to="/shop" data-testid="product-back-link" className="mx-5 mb-6 inline-flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#5C5C5C] transition-colors hover:text-[#1E1E1E] lg:mx-0">
          <ArrowLeft size={14} /> Back to collection
        </Link>
        <div className="grid min-w-0 grid-cols-1 lg:grid-cols-12 lg:gap-10">
          <div className="min-w-0 lg:col-span-7">
            <MobileGallery key={selected?.id || 'default'} images={images} name={product.name} />
            <DesktopGallery images={images} name={product.name} />
          </div>

          <ProductDetails product={product} variantState={variantState} onAdd={addToCart} />
        </div>
      </div>
      <Footer />
    </main>
  );
}